import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../../services/ApiService';
import {
  issuePassportSubjectScanToken,
  listPassportSubjectScanTokenBundle,
  revokePassportSubjectScanToken,
  type PassportIssuedScanToken,
  type PassportScanTokenPrintable,
  type PassportScanTokenSummary,
  type PassportSubjectType,
} from '../../services/api/passport';
import PassportStickerStudio from './PassportStickerStudio';
import { normalizePassportStickerSubjectLabel } from '../../utils/passportStickerCanvas';

type Props = {
  subjectType: Extract<PassportSubjectType, 'shop' | 'club' | 'event' | 'place'>;
  subjectId: number;
  /** Footer label on printed stickers (shop name, club name, etc.). */
  subjectLabel: string;
  heading?: string;
};

function subjectUsesSingleActiveToken(subjectType: Props['subjectType']): boolean {
  return subjectType === 'shop' || subjectType === 'club' || subjectType === 'place';
}

function printableFromIssued(issued: PassportIssuedScanToken): PassportScanTokenPrintable | null {
  if (!issued.qr_svg && !issued.qr_svg_inverted) return null;
  return {
    qr_svg: issued.qr_svg ?? null,
    qr_svg_inverted: issued.qr_svg_inverted ?? null,
    scan_url: issued.scan_url,
  };
}

function summaryFromIssued(issued: PassportIssuedScanToken): PassportScanTokenSummary {
  return {
    ...issued.token,
    status: issued.token.status || 'active',
    use_count: issued.token.use_count ?? 0,
    requires_location: issued.token.requires_location ?? false,
    geofence_radius_m: issued.token.geofence_radius_m ?? null,
    max_uses: issued.token.max_uses ?? null,
    expires_at: issued.token.expires_at ?? null,
    created_at: issued.token.created_at ?? new Date().toISOString(),
  };
}

/**
 * Mint / print / revoke Passport stickers for a shop, club, or event hunt subject.
 * Vehicle builds use the Share / build QR instead (one code per vehicle).
 * Shops and clubs keep one active code; managers can re-download the same sticker.
 */
const SubjectPassportQrPanel: React.FC<Props> = ({
  subjectType,
  subjectId,
  subjectLabel,
  heading = 'Passport QR',
}) => {
  const [tokens, setTokens] = useState<PassportScanTokenSummary[]>([]);
  const [savedPrintable, setSavedPrintable] = useState<{
    token: PassportScanTokenSummary;
    printable: PassportScanTokenPrintable;
  } | null>(null);
  const [reprintSupported, setReprintSupported] = useState<boolean | null>(null);
  const [apiStickerLabel, setApiStickerLabel] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [issueBusy, setIssueBusy] = useState(false);
  const [revokeBusyId, setRevokeBusyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const singleActiveToken = subjectUsesSingleActiveToken(subjectType);
  const activeToken = useMemo(
    () => tokens.find((token) => token.status === 'active') ?? null,
    [tokens]
  );

  const applyPrintableFromToken = useCallback((token: PassportScanTokenSummary) => {
    if (token.printable) {
      setSavedPrintable({ token, printable: token.printable });
    }
  }, []);

  const load = useCallback(async () => {
    if (!subjectId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const bundle = await listPassportSubjectScanTokenBundle(subjectType, subjectId);
      const nextTokens = subjectUsesSingleActiveToken(subjectType)
        ? bundle.tokens.filter((token) => token.status === 'active')
        : bundle.tokens;
      setTokens(nextTokens);
      setReprintSupported(bundle.reprint_supported === true);
      setApiStickerLabel(bundle.sticker_label ?? null);
      const active = nextTokens.find((token) => token.status === 'active') ?? null;
      if (active?.printable) {
        setSavedPrintable({ token: active, printable: active.printable });
      } else if (!active) {
        setSavedPrintable(null);
      }
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : 'Could not load Passport codes');
    } finally {
      setLoading(false);
    }
  }, [subjectType, subjectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleIssue = async () => {
    setIssueBusy(true);
    setActionError(null);
    try {
      const result = await issuePassportSubjectScanToken({
        subject_type: subjectType,
        subject_id: subjectId,
      });
      const printable = printableFromIssued(result);
      const tokenSummary = summaryFromIssued(result);
      if (printable) {
        setSavedPrintable({ token: tokenSummary, printable });
      }
      setTokens((prev) =>
        subjectUsesSingleActiveToken(subjectType)
          ? [tokenSummary]
          : [tokenSummary, ...prev.filter((t) => t.id !== tokenSummary.id)]
      );
      if (result.reprint_available === false) {
        setReprintSupported(false);
      } else if (result.reprint_available === true) {
        setReprintSupported(true);
      }
      if (result.sticker_label) {
        setApiStickerLabel(result.sticker_label);
      }
      void apiService.trackEvent('passport_qr_issued', {
        metadata: { subject_type: subjectType },
      });
      await load();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Could not create Passport QR');
    } finally {
      setIssueBusy(false);
    }
  };

  const handleRevoke = async (tokenId: number) => {
    if (
      !window.confirm(
        'Revoke this Passport QR? Existing stickers with this code will stop working.'
      )
    ) {
      return;
    }
    setRevokeBusyId(tokenId);
    setActionError(null);
    try {
      await revokePassportSubjectScanToken({
        subject_type: subjectType,
        subject_id: subjectId,
        token_id: tokenId,
        reason: 'subject_manager_revoked',
      });
      if (savedPrintable?.token.id === tokenId) {
        setSavedPrintable(null);
      }
      await load();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Could not revoke code');
    } finally {
      setRevokeBusyId(null);
    }
  };

  const studioProps = savedPrintable
    ? {
        qrSvg: savedPrintable.printable.qr_svg,
        qrSvgInverted: savedPrintable.printable.qr_svg_inverted,
        scanUrl: savedPrintable.printable.scan_url,
      }
    : null;

  const stickerFooterLabel =
    normalizePassportStickerSubjectLabel(subjectLabel) ??
    normalizePassportStickerSubjectLabel(apiStickerLabel);

  const showCreateButton = !singleActiveToken || !activeToken;

  const legacyReprintMessage =
    reprintSupported === false
      ? 'Sticker reprint is not available on this server yet. Print or download immediately after creating a new QR. If you already left this page, ask a platform admin to run the latest database migration (20260821_passport_scan_token_reprint), then revoke and create once more.'
      : 'This Passport QR was created before reprint was available. Revoke it and create a new one to download a printable sticker. The code will change, so replace any old stickers you no longer want scanned.';

  return (
    <section className="rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 p-4 sm:p-5 space-y-4">
      <div>
        <h2 className="heading-section text-sm">{heading}</h2>
        <p className="text-muted text-sm mt-1">
          Print a Passport sticker for this {subjectType}. Collectors keep its card after signing
          in. A sticker scan records QR collection; confirmed attendance needs location or staff
          verification.
          {singleActiveToken ? ` This ${subjectType} keeps one active Passport QR.` : null}
        </p>
      </div>

      {loadError ? (
        <div className="alert-error text-sm" role="alert">
          {loadError}
        </div>
      ) : null}
      {actionError ? (
        <div className="alert-error text-sm" role="alert">
          {actionError}
        </div>
      ) : null}
      {showCreateButton ? (
        <button
          type="button"
          className="btn-primary"
          disabled={issueBusy || loading}
          onClick={() => void handleIssue()}
        >
          {issueBusy ? 'Creating…' : 'Create Passport QR'}
        </button>
      ) : null}
      {studioProps ? (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 space-y-4">
          <p className="text-sm text-amber-200">
            Your active Passport sticker. Download or print it again anytime.
          </p>
          <PassportStickerStudio
            title={`${subjectType} Passport sticker`}
            subjectLabel={stickerFooterLabel}
            qrSvg={studioProps.qrSvg}
            qrSvgInverted={studioProps.qrSvgInverted}
            scanUrl={studioProps.scanUrl}
          />
          {singleActiveToken && activeToken ? (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-1 border-t border-amber-500/15">
              <p className="text-xs text-gray-400">
                Code <span className="font-mono text-meta">{activeToken.token_fingerprint}</span>
                {activeToken.use_count > 0 ? ` · ${activeToken.use_count} uses` : null}
              </p>
              <button
                type="button"
                className="btn-danger"
                disabled={revokeBusyId === activeToken.id}
                onClick={() => void handleRevoke(activeToken.id)}
              >
                {revokeBusyId === activeToken.id ? 'Revoking…' : 'Revoke QR'}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {singleActiveToken && activeToken && !studioProps ? (
        <div className="space-y-3">
          <p className="text-muted text-sm leading-relaxed">{legacyReprintMessage}</p>
          <button
            type="button"
            className="btn-danger"
            disabled={revokeBusyId === activeToken.id}
            onClick={() => void handleRevoke(activeToken.id)}
          >
            {revokeBusyId === activeToken.id ? 'Revoking…' : 'Revoke QR'}
          </button>
        </div>
      ) : null}
      {loading ? (
        <p className="text-muted text-sm" aria-busy="true">
          Loading codes…
        </p>
      ) : !singleActiveToken && tokens.length > 0 ? (
        <ul className="space-y-2" aria-label="Existing Passport scan codes">
          {tokens.map((token) => (
            <li
              key={token.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 px-3 py-2"
            >
              <div className="min-w-0 text-sm">
                <span className="font-mono text-xs text-meta">{token.token_fingerprint}</span>
                <span className="mx-2 text-meta">·</span>
                <span className="badge">{token.status}</span>
                <span className="mx-2 text-meta">·</span>
                <span className="text-muted">{token.use_count} uses</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {token.status === 'active' && token.printable ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => applyPrintableFromToken(token)}
                  >
                    Show sticker
                  </button>
                ) : null}
                {token.status === 'active' ? (
                  <button
                    type="button"
                    className="btn-danger"
                    disabled={revokeBusyId === token.id}
                    onClick={() => void handleRevoke(token.id)}
                  >
                    {revokeBusyId === token.id ? 'Revoking…' : 'Revoke'}
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : !singleActiveToken ? (
        <p className="text-muted text-sm">No Passport QR codes yet.</p>
      ) : !loading && !activeToken ? (
        <p className="text-muted text-sm">No Passport QR codes yet.</p>
      ) : null}
    </section>
  );
};

export default SubjectPassportQrPanel;
